import DragContainer from "@/components/helpers/DragNDrop/DragContainer";
import EditorDragItem from "@/components/helpers/DragNDrop/EditorDragItem";
import DragActiveItemContainer from "@/components/helpers/DragNDrop/DragActiveItemContainer";
import DragTabContainer from "@/components/helpers/DragNDrop/DragTabContainer";
import DragSubmitButton from "@/components/helpers/DragNDrop/DragSubmitButton";
import Drag from "@/classes/Drag";

/*
 * Automatically add required functionality for drag'n'drop
 * Import mixin into component, pass a structure, and start using drag components.
 */
export default {
  props: {
    structure: {
      type: Array,
      required: true
    },
    // Array of elements that can be dropped into editor.
    // Should be an array containing Objects. Each Object
    // must have a title and an items Array
    dragElements: {
      type: Array,
      required: true
    },
    defaultElements: {
      type: Array
    },
    // Used for example for lead sheet elements
    extraElements: {
      type: Array
    },
    projectId: {
      required: false
    },
    showTabs: {
      type: Boolean,
      default: false
    },
    shouldIncludeSubmit: {
      // Include a submit button outside the normal editor that cannot be moved but can be edited.
      // Also removed any existing submit buttons in structure.
      type: Boolean,
      default: false
    }
  },
  components: {
    DragContainer,
    DragActiveItemContainer,
    DragTabContainer,
    DragSubmitButton,
    EditorDragItem
  },
  data() {
    return {
      internalDragElements: [],
      internalTabs: [],
      internalElementsToRemove: [
        "submit",
        "grid_container",
        "container_top",
        "container_bottom",
        "container_grid",
        "container_tab",
        "container"
      ],
      defaultTabElement: {
        element_type: "container_tab",
        parent_element: "root",
        name: "main_tab",
        label: "Tab 1"
      }
    };
  },
  computed: {
    internalStructure: {
      get: function() {
        return this.$store.getters.getDragEditorCurrentStructure;
      },
      set: function(val) {
        this.$store.commit("SET_DRAG_EDITOR_CURRENT_STRUCTURE", val);
      }
    },
    internalSubmitButton: {
      get: function() {
        return this.$store.getters.getDragEditorSubmitButton;
      },
      set: function(val) {
        this.$store.commit("SET_DRAG_EDITOR_SUBMIT_BUTTON", val);
      }
    },
    activeTab() {
      let activeTab = this.$store.getters.getDragEditorActiveTab;
      if (activeTab) {
        if (Object.keys(activeTab).length > 0) {
          return activeTab;
        } else {
          // Fall back to first tab
          return this.internalTabs[0];
        }
      }
    },
    internalStructureByTab() {
      if (!this.activeTab) {
        return this.internalStructure;
      }
      return this.internalStructure.filter(element => {
        return element.tab === this.activeTab.name;
      });
    }
  },
  mounted() {
    this.setupTabs();
    this.resetInternalStructure();
    this.setupDragElements();

    if (this.shouldIncludeSubmit) {
      this.setupSubmit();
    }
  },
  methods: {
    setupDragElements() {
      // Build internalDragElements from the ones received through props
      // Filter and set internally required values
      this.internalDragElements = [];     
      this.dragElements.forEach(object => {
        // building the dynamic elements for the editor which come from the lead sheet
        if (!Array.isArray(object) && object.shouldBeFiltered) {
          let items = object.items;
          this.internalDragElements.push({
            title: object.title,
            items: this.buildElements(items, true)
          });

        // building the standard elements
        } else {
          object.forEach(itemGroup => {
            this.internalDragElements.push({
              title: itemGroup.title,
              items: this.buildElements(itemGroup.items, false)
            });
          });
        }
      });
    },
    setupTabs() {
      // Find all tabs in structure and setup internaltabs
      let tabs = _.clone(this.structure).filter(
        e => e.element_type === "container_tab"
      );
      this.setTabs(tabs);
    },
    setupSubmit() {
      // Find existing submit button if present in structure and move it outside the editor itself
      let existingSubmit = this.structure.find(e =>
        ["submit", "submit_button"].includes(e.element_type)
      );
      if (existingSubmit) {
        existingSubmit.element_type = "submit";
        existingSubmit.parent_element = this.activeTab.name;
        this.internalSubmitButton = existingSubmit;
      } else {
        this.internalSubmitButton = Drag.generateDefaultSubmitButton();
      }
    },
    editElement(element) {
      let e = this.findElementByPositionAndName(element);
      let idx = this.internalStructure.indexOf(e);
      this.internalStructure[idx] = element;
      this.setIsEdited();
    },
    deleteElement(element) {
      let e = this.findElementByPositionAndName(element);
      let idx = this.internalStructure.indexOf(e);

      // remove tab and parent_element from element
      if(element.tab) {
        delete element.parent_element
        delete element.tab
      }

      // if this element is a tab remove the tab and all elements that belong to it
      if (
        this.showTabs &&
        element.element_type == "container_tab" &&
        element.parent_element == "root"
      ) {
        return this.deleteTabAndBelongingElements(element);
      }

      // for other elements
      if (idx === -1) {
        throw "Element was not found in internalStructure";
      }
     
      this.spliceInternalStructure(idx, 1);
      if (element.shouldBeDeleted && this.internalDragElements) {
        let dragElements = this.internalDragElements.find(object => {
          return object.title === element.parentTitle;
        });
        if (dragElements) {
          dragElements.items.push(element);
        }
      }
    },
    deleteTabAndBelongingElements(element) {
      let newStructure = _.cloneDeep(this.internalStructure);
      let newTabs = _.cloneDeep(this.internalTabs);

      // Remove all elements that belong to the Tab
      let internalStructureOnTab = newStructure.filter(t => {
        return t.tab === element.name;
      });
      internalStructureOnTab.forEach(function(e) {
        _.remove(newStructure, e);
      });
      this.internalStructure = newStructure;

      let tab = newTabs.find(t => {
        return t.name === element.name;
      });
      _.remove(newTabs, tab);
      this.internalTabs = newTabs;
    },
    findElementByPositionAndName(element) {
      return this.internalStructure.find(
        e =>
          e.tab === element.tab &&
          e.name === element.name &&
          e.element_type === element.element_type
      );
    },
    setTabs(tabs) {
      // Clear tabs and set to what it passed as argument or defaultTab
      let tabArray = [];
      if (tabs.length > 0) {
        tabs.forEach(tab => {
          tabArray.push(tab);
        });
      } else {
        // Push a default tab
        tabArray.push(this.defaultTabElement);
      }
      this.internalTabs = tabArray;
    },
    resetInternalStructure() {
      let cloneStructure = _.cloneDeep(this.structure);
      // Set correct tabs and then remove top/bottom elements
      cloneStructure.forEach((item, idx) => {
        if (item.element_type === "submit_button") {
          return;
        }

        // We need some logic for assigning which tabs each element should go under.
        // An element can have another element as its parent_element. If this happens
        // we have to go further up to find the correct tab.
        item.tab = this.findTabInStructure(item, cloneStructure);

        item = this.sanitizeDragItem(item);
      });
      cloneStructure = this.removeElements(cloneStructure);

      this.internalStructure = cloneStructure;
    },
    sanitizeDragItem(item) {
      if (!this.showTabs) {
        item = Drag.sanitizeOldFormElements(
          item,
          this.internalTabs,
          this.activeTab
        );
      }
      if (item["class"] && item["class"].includes("hidden")) {
        item.hidden = true;
        item["class"] = item["class"].replace(/hidden/, "");
      }
      return item;
    },
    findTabInStructure(item, structure) {
      // Return name of top most tab for item

      let parentElement = structure.find(element => {
        return element.name === item.parent_element;
      });

      if (!parentElement) {
        // We reached the top of the stack so fall back to first container
        return this.internalTabs[0].name;
      } else if (parentElement.element_type === "container_tab") {
        return parentElement.name;
      } else {
        return this.findTabInStructure(parentElement, structure);
      }
    },
    buildElements(array, belongsToLeadSheet) {
      let cloneArray = _.cloneDeep(array);
      cloneArray.forEach((item, idx) => {
        item.belongsToLeadSheet = belongsToLeadSheet;
        // Make sure label is set
        if (!item.label && item.name) {
          item.label = item.name.replace(/_/g, " ");
        }
      });
      cloneArray = this.removeElements(cloneArray);      
      return cloneArray;
    },
    removeElements(array) {
      // Remove the element if needed or assert that correct tab is set
      return array.filter(
        item => !this.internalElementsToRemove.includes(item.element_type)
      );
    },
    validateDrop(dropResult) {
      let { addedIndex, removedIndex, payload } = dropResult;

      // we have to overwrite the addedIndex and removedIndex if we are in tabs
      if (dropResult.payload.isInTab && dropResult.payload.collectionByTab) {
        let itemAddedInCollection =
          dropResult.payload.collectionByTab[addedIndex];

        if (itemAddedInCollection) {
          dropResult.addedIndex = dropResult.payload.collection.indexOf(
            itemAddedInCollection
          );
          dropResult.removedIndex = dropResult.payload.collection.indexOf(
            dropResult.payload
          );
        } else {
          // if there is no item added yet
          let itemAddedInCollection = dropResult.payload;

          dropResult.addedIndex = dropResult.payload.collection.indexOf(
            itemAddedInCollection
          );
          dropResult.removedIndex = dropResult.payload.collection.indexOf(
            dropResult.payload
          );
        }

        delete dropResult.payload.collectionByTab;
        delete dropResult.payload.isInTab;
      }

      // If addedIndex is null item was not dropped into editor
      // Also in Javascript !0 is true ...
      if (addedIndex != 0 && !addedIndex) {
        return false;
      }
      // Dropped in same position
      if (addedIndex === removedIndex) {
        return false;
      }
      return true;
    },
    onDrop(dropResult) {             
      let canDrop = this.validateDrop(dropResult);            
      if (!canDrop) {
        if (dropResult.payload) {
          delete dropResult.payload.collection;
          delete dropResult.payload.collectionIdx;
        }
        return;
      }

      // We have to clone the object otherwise we can have multiple objects
      // that share the same pointer and therefore get updated together
      let droppedItem = _.clone(dropResult.payload);
      // Remove item from start position and then add back into array at dropped position
      let startIdx = dropResult.addedIndex;

      if (droppedItem.shouldBeDeleted) {
        droppedItem = this.removeDroppedItemFromOrigin(dropResult);
        if (droppedItem.class && droppedItem.class.length > 0) {
          droppedItem.class = "";
        }
      }

      if (!droppedItem.parent_element) {
        droppedItem.parent_element = this.activeTab.name;
        droppedItem.tab = this.activeTab.name;
      }
      if (!droppedItem.tab) {
        droppedItem.tab = this.findTabInStructure(
          droppedItem,
          this.internalStructure
        );
      }
      if (droppedItem.element_type.includes("_custom")) {
        droppedItem.element_type = droppedItem.element_type.replace(
          /_custom/,
          ""
        );
      }
      if (!droppedItem.name) {
        // Generate a random name if none is set
        droppedItem.name = `${droppedItem.element_type}-${Math.random()
          .toString(36)
          .substring(7)}`;
      }

      // remove not needed properties
      delete droppedItem.collectionDrag;
      delete droppedItem.collection;
      delete droppedItem.collectionIdx;
      delete droppedItem.parentTitle;

      this.spliceInternalStructure(startIdx, 0, droppedItem);

      this.setActiveItem(droppedItem);
      this.setIsEdited();      
    },
    spliceInternalStructure(startIdx, deleteAmount, item) {

      if (startIdx == -1) {
        startIdx = this.internalStructure.length;
      }

      let newStructure = _.cloneDeep(this.internalStructure);
      if (item) {
        newStructure.splice(startIdx, deleteAmount, item);
      } else {
        newStructure.splice(startIdx, deleteAmount);
      }
      this.internalStructure = newStructure;
    },
    setIsEdited() {
      this.$store.commit("SET_DRAG_EDITOR_EDITED", true);
    },
    setActiveItem(item) {
      this.$store.commit("SET_DRAG_EDITOR_ACTIVE", item);
    },
    removeDroppedItemFromOrigin(dropResult) {
      // Remove a dropped item from it's origin in internalDragElements
      // and clean up the resulting dropResult
      let startIdx = dropResult.payload.collectionIdx;

      if (startIdx == -1) {
        if (dropResult.payload.collectionDrag) {
          _.remove(dropResult.payload.collectionDrag, dropResult.payload);
        }
      } else {
        if (dropResult.payload.collection == this.internalStructure) {
          this.spliceInternalStructure(startIdx, 1);
        } else {
          dropResult.payload.collection.splice(startIdx, 1);
        }
      }

      // We have to remove collection or we will get an infinite loop when item is selected
      delete dropResult.payload.collectionDrag;
      delete dropResult.payload.collection;
      delete dropResult.payload.collectionIdx;
      return dropResult.payload;
    }
  },
  watch: {
    structure() {
      this.setupTabs();
      this.resetInternalStructure();
      if (this.shouldIncludeSubmit) {
        this.setupSubmit();
      }
    },
    dragElements() {
      this.setupDragElements();
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/mixins/Drag.js